import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { PixelPlaceholderLoader, QueryBarItemSingle, Tooltip, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { IAutoCompleteData } from "app/components/campaigns/queryBar-report/CampaignsQueryBarAutocomplete";
import { CampaignsQueryBarAutocomplete } from "app/components/campaigns/queryBar-report/CampaignsQueryBarAutocomplete";
import { SHARED_ICON_NAME } from "app/components/campaigns/shared/CampaignsConst";
import { QueryBarItem } from "app/components/compare/QueryBarItem";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { translate } from "app/filters/i18nFilter";
import { CompetitorsIndicator } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/CompetitorsIndicator";
import { getMaxCampaigns } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { AssetPermissions, useAssetPermissions } from "app/services/asset-permissions/AssetPermissions";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";

type LLMCampaignsQueryBarProps = {
    campaigns?: IAiTrackerCampaign[];
    shouldDefaultOpen?: boolean;
    badgeComponent?: React.ReactNode;
    hideItemSecondaryText?: boolean;
    onSearchItemClick?: (campaign: IAiTrackerCampaign) => void;
    queryBarPadding?: string;
};

export const LLMCampaignsQueryBar = ({
    campaigns = [],
    shouldDefaultOpen = false,
    onSearchItemClick,
    badgeComponent,
    hideItemSecondaryText,
    queryBarPadding = "0 16px",
}: LLMCampaignsQueryBarProps) => {
    const {
        params: { campaignId },
    } = useCurrentStateAndParams();

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const hasAutoOpenedRef = useRef(false);
    const selectedCampaign = campaigns.find((campaign) => campaign.id === campaignId);
    const selectedCampaignPermissions = useAssetPermissions(selectedCampaign);

    const maxCampaigns = getMaxCampaigns();
    const isCampaignsCountClaimValid = maxCampaigns !== null && maxCampaigns > 0;

    const handleBodyClick = useCallback(() => {
        if (isSearchOpen) {
            setIsSearchOpen(false);
        }
    }, [isSearchOpen]);

    useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, { capture: true });
        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, [handleBodyClick]);

    useEffect(() => {
        if (!shouldDefaultOpen || hasAutoOpenedRef.current) {
            return;
        }
        hasAutoOpenedRef.current = true;
        setIsSearchOpen(true);
    }, [shouldDefaultOpen]);

    const selectedCampaignSubtitle = translate("llmTracker.campaignQuery.selectedItem.subtitle", {
        count: selectedCampaign?.topics?.length ?? 0,
    });

    const itemComponent = (
        <Flex
            alignItems="center"
            height="100%"
            padding={queryBarPadding}
        >
            <QueryBarItemSingle
                text={selectedCampaign?.name}
                secondaryText={
                    hideItemSecondaryText || !selectedCampaign?.topics?.length ? null : selectedCampaignSubtitle
                }
                image={selectedCampaign?.primaryDomain?.favicon}
                onItemClick={() => setIsSearchOpen(true)}
                iconName={"arrow"}
                iconSize={"8px"}
            />
        </Flex>
    );

    const handleSearchItemClick = (selectedMappedCampaign: IAutoCompleteData) => {
        if (!selectedMappedCampaign) {
            return;
        }

        const originalCampaign = campaigns.find((campaign) => campaign.id === selectedMappedCampaign.id);

        if (!originalCampaign) {
            return;
        }

        if (!isCampaignsCountClaimValid && !originalCampaign.isDemo) {
            onHookClick("AiTracker", "digitalsuite_llm_solutions_homepage", EHooksVersions.V5);
            return;
        }

        const campaignName = originalCampaign.name;
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Header/Campaign/${campaignName}`);
        if (originalCampaign.isDemo) {
            allTrackers.dispatchTrackEvent("Button", "click", `Demo Campaign/Query Bar/${campaignName}`);
        }

        setIsSearchOpen(false);
        onSearchItemClick?.(originalCampaign);
    };

    const handleFocus = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "open", `Header/Select Campaign`);
    };

    const campaignsConfigurations = useMemo(
        () =>
            campaigns.map((campaign) => {
                const permissions = new AssetPermissions(campaign);
                return {
                    ...campaign,
                    asset: campaign.primaryDomain?.favicon ?? "",
                    isShared: !permissions.isOwner,
                } as IAutoCompleteData;
            }),
        [campaigns],
    );

    const searchComponent = (
        <CampaignsQueryBarAutocomplete
            onClick={handleSearchItemClick}
            campaignsConfigurations={campaignsConfigurations}
            onFocus={handleFocus}
            userCampaignsShouldBeHooked={!isCampaignsCountClaimValid}
        />
    );

    const loadingComponent = (
        <PixelPlaceholderLoader
            width={140}
            height={26}
        />
    );

    return (
        <Flex
            alignItems="center"
            gap="8px"
            width="100%"
        >
            <QueryBarItem
                id={campaignId}
                activeItem={isSearchOpen ? campaignId : null}
                renderComponent={itemComponent}
                searchComponent={searchComponent}
                loadingComponent={loadingComponent}
            />
            <Flex
                gap="8px"
                alignItems="center"
            >
                {badgeComponent}
                <CompetitorsIndicator competitors={selectedCampaign?.competitors ?? []} />
                {selectedCampaign && !selectedCampaignPermissions?.isOwner ? (
                    <Tooltip
                        content={translate("llm.campaigns.shared.icon.tooltip", {
                            owner: selectedCampaign.ownerUserName,
                        })}
                    >
                        <div>
                            <IconButton
                                dataAutomation="llm-shared-button"
                                iconSize="sm"
                                iconName={SHARED_ICON_NAME}
                                type="flat"
                            />
                        </div>
                    </Tooltip>
                ) : null}
            </Flex>
        </Flex>
    );
};
